import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Annotations,
  api,
  candidatesOf,
  clock,
  Cue,
  Peaks,
  Track,
} from "../api";
import { AppEpisode, route } from "./api";
import { useApp, persist } from "./context";
import { Artwork, Icon, IconButton, LanguageSelect, Modal } from "./visual";
import Devices from "./Devices";
export function cueText(cue: Cue, lang: string) {
  return lang === "original" || lang === cue.lang
    ? cue.text
    : cue.translations[lang]?.text || cue.text;
}
export function cueLanguage(cue: Cue, lang: string) {
  return lang === "original" || !cue.translations[lang] ? cue.lang : lang;
}
export function trackIdentity(track: Track) {
  const c = candidatesOf(track)[0];
  return {
    artist: track.artist || c?.artist || "",
    title: track.title || c?.title || "",
  };
}
export function currentTrack(tracks: Track[], ms: number) {
  return [...tracks]
    .filter(
      (t) =>
        t.start_ms !== null &&
        t.start_ms <= ms &&
        (t.end_ms === null || ms < t.end_ms),
    )
    .sort((a, b) => (b.start_ms || 0) - (a.start_ms || 0))[0];
}
function usePlayerState() {
  const { catalogue, notify, t } = useApp();
  const audio = useRef<HTMLAudioElement | null>(null);
  const [episode, setEpisode] = useState<AppEpisode | null>(null),
    [playing, setPlaying] = useState(false),
    [ms, setMs] = useState(0),
    [duration, setDuration] = useState(0),
    [volume, setVolume] = useState(0.8),
    [speed, setSpeed] = useState(1),
    [buffering, setBuffering] = useState(false),
    [error, setError] = useState("");
  const [annotations, setAnnotations] = useState<Annotations | null>(null),
    [peaks, setPeaks] = useState<Peaks | null>(null),
    [queue, setQueue] = useState<string[]>([]),
    [queueOpen, setQueueOpen] = useState(false);
  const [offscreenEpisode, setOffscreenEpisode] = useState<string | null>(null),
    [devicesOpen, setDevicesOpen] = useState(false);
  const current = useRef<AppEpisode | null>(null),
    pending = useRef<number | null>(null),
    wantPlay = useRef(false),
    queueRef = useRef(queue),
    saveAt = useRef(0),
    speedRef = useRef(speed);
  queueRef.current = queue;
  speedRef.current = speed;
  useEffect(() => {
    const a = audio.current;
    if (!a) return;
    a.volume = volume;
  }, [volume]);
  useEffect(() => {
    const a = audio.current;
    if (a) a.playbackRate = speed;
  }, [speed]);
  useEffect(() => {
    let active = true;
    setAnnotations(null);
    setPeaks(null);
    if (!episode) return;
    api
      .annotations(episode.id, false)
      .then((v) => {
        if (active) setAnnotations(v);
      })
      .catch((e) => {
        if (active) notify(e.message);
      });
    api
      .peaks(episode.id)
      .then((v) => {
        if (active) setPeaks(v);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [episode?.id]);
  function savePosition() {
    const ep = current.current,
      a = audio.current;
    if (ep?.recording && a && Number.isFinite(a.currentTime))
      persist("ew-position-" + ep.recording.id, String(a.currentTime * 1000));
  }
  async function attemptPlay() {
    const a = audio.current;
    if (!a) return;
    try {
      await a.play();
      setError("");
    } catch (e) {
      if ((e as Error).name !== "AbortError") {
        setBuffering(false);
        setError(
          (e as Error).name === "NotAllowedError"
            ? "Press play to start listening."
            : "Playback could not start. Try play again.",
        );
      }
    }
  }
  function load(ep: AppEpisode, at?: number, autoplay = true) {
    const a = audio.current;
    if (!a || !ep.recording) return;
    if (current.current?.id === ep.id) {
      if (at !== undefined) seek(at);
      if (autoplay) void attemptPlay();
      return;
    }
    savePosition();
    a.pause();
    setError("");
    setBuffering(autoplay);
    current.current = ep;
    setEpisode(ep);
    setDuration(ep.recording.duration_ms);
    setPlaying(false);
    let resume = 0;
    try {
      resume = Number(
        localStorage.getItem("ew-position-" + ep.recording.id) || 0,
      );
    } catch {}
    const position = Math.max(
      0,
      Math.min(
        at ?? (resume < ep.recording.duration_ms - 5000 ? resume : 0),
        ep.recording.duration_ms,
      ),
    );
    pending.current = position;
    wantPlay.current = autoplay;
    setMs(position);
    a.src = api.audioUrl(ep.recording.id);
    a.load();
    if (autoplay) void attemptPlay();
  }
  function seek(value: number) {
    const a = audio.current;
    if (!a) return;
    const next = Math.max(
      0,
      Math.min(value, duration || current.current?.recording?.duration_ms || 0),
    );
    if (a.readyState >= 1) {
      a.currentTime = next / 1000;
      setMs(next);
    } else pending.current = next;
  }
  function toggle() {
    const a = audio.current;
    if (!a) return;
    if (a.paused) void attemptPlay();
    else a.pause();
  }
  function next() {
    const id = queueRef.current[0];
    if (!id) return;
    setQueue((q) => q.slice(1));
    const ep = catalogue.episodes.find((e) => e.id === id);
    if (ep) load(ep, 0);
  }
  const latest = useRef({ toggle, seek, next });
  latest.current = { toggle, seek, next };
  useEffect(() => {
    const a = audio.current;
    if (!a) return;
    const update = () => {
      setMs(a.currentTime * 1000);
      if (Date.now() - saveAt.current > 5000) {
        savePosition();
        saveAt.current = Date.now();
      }
    };
    const loaded = () => {
      if (Number.isFinite(a.duration)) setDuration(a.duration * 1000);
      if (pending.current !== null) {
        a.currentTime = pending.current / 1000;
        pending.current = null;
      }
      a.playbackRate = speedRef.current;
      if (wantPlay.current) {
        wantPlay.current = false;
        void attemptPlay();
      }
      setBuffering(false);
    };
    const onPlay = () => {
        setPlaying(true);
        setBuffering(false);
      },
      onPause = () => {
        setPlaying(false);
        setBuffering(false);
        savePosition();
      },
      waiting = () => setBuffering(true),
      ready = () => setBuffering(false),
      failed = () => {
        setError(
          "The recording could not be loaded. Try another programme or retry playback.",
        );
        setPlaying(false);
        setBuffering(false);
      },
      ended = () => {
        savePosition();
        setPlaying(false);
        latest.current.next();
      };
    const events: Record<string, () => void> = {
      timeupdate: update,
      loadedmetadata: loaded,
      play: onPlay,
      playing: onPlay,
      pause: onPause,
      waiting,
      canplay: ready,
      error: failed,
      ended,
    };
    Object.entries(events).forEach(([event, fn]) =>
      a.addEventListener(event, fn),
    );
    window.addEventListener("pagehide", savePosition);
    let frame = 0,
      last = 0;
    const animate = (now: number) => {
      if (!a.paused && now - last > 100) {
        setMs(a.currentTime * 1000);
        last = now;
      }
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => {
      Object.entries(events).forEach(([event, fn]) =>
        a.removeEventListener(event, fn),
      );
      window.removeEventListener("pagehide", savePosition);
      cancelAnimationFrame(frame);
    };
  }, []);
  useEffect(() => {
    if (!("mediaSession" in navigator) || !episode) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: episode.title,
      artist: episode.show_title || episode.station_name,
      album: episode.station_name,
    });
    navigator.mediaSession.setActionHandler("play", () => void attemptPlay());
    navigator.mediaSession.setActionHandler("pause", () =>
      audio.current?.pause(),
    );
    navigator.mediaSession.setActionHandler("seekbackward", () =>
      latest.current.seek((audio.current?.currentTime || 0) * 1000 - 15000),
    );
    navigator.mediaSession.setActionHandler("seekforward", () =>
      latest.current.seek((audio.current?.currentTime || 0) * 1000 + 15000),
    );
    navigator.mediaSession.setActionHandler("seekto", (e) =>
      latest.current.seek((e.seekTime || 0) * 1000),
    );
    navigator.mediaSession.setActionHandler("nexttrack", () =>
      latest.current.next(),
    );
  }, [episode]);
  useEffect(() => {
    if ("mediaSession" in navigator)
      navigator.mediaSession.playbackState = playing ? "playing" : "paused";
  }, [playing]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!current.current || e.altKey || e.ctrlKey || e.metaKey) return;
      const el = e.target as HTMLElement;
      if (
        el.closest(
          "input,textarea,select,button,a,[role=slider],[contenteditable=true],dialog",
        )
      )
        return;
      if (e.code === "Space") {
        e.preventDefault();
        latest.current.toggle();
      }
      if (e.code === "ArrowRight") {
        e.preventDefault();
        latest.current.seek((audio.current?.currentTime || 0) * 1000 + 15000);
      }
      if (e.code === "ArrowLeft") {
        e.preventDefault();
        latest.current.seek((audio.current?.currentTime || 0) * 1000 - 15000);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  function enqueue(ep: AppEpisode) {
    setQueue((q) => [...q, ep.id]);
    notify(t("added"));
  }
  return {
    audio,
    episode,
    playing,
    ms,
    duration,
    volume,
    setVolume,
    speed,
    setSpeed,
    buffering,
    error,
    annotations,
    peaks,
    queue,
    setQueue,
    queueOpen,
    setQueueOpen,
    load,
    seek,
    toggle,
    next,
    enqueue,
    offscreenEpisode,
    setOffscreenEpisode,
    devicesOpen,
    setDevicesOpen,
  };
}
const Context = createContext<ReturnType<typeof usePlayerState> | null>(null);
export function PlayerProvider({ children }: { children: ReactNode }) {
  const value = usePlayerState();
  return (
    <Context.Provider value={value}>
      <audio ref={value.audio} preload="metadata" x-webkit-airplay="allow" />
      {children}
      {value.devicesOpen && <Devices />}
    </Context.Provider>
  );
}
export function usePlayer() {
  const p = useContext(Context);
  if (!p) throw Error("PlayerProvider missing");
  return p;
}
export function PlayButton({
  episode,
  large = false,
}: {
  episode: AppEpisode;
  large?: boolean;
}) {
  const p = usePlayer(),
    { t } = useApp();
  const active = p.episode?.id === episode.id && p.playing;
  return (
    <button
      className={large ? "button primary" : "round-play"}
      disabled={!episode.recording}
      aria-label={active ? t("pause") : t("listen") + " · " + episode.title}
      onClick={() =>
        p.episode?.id === episode.id ? p.toggle() : p.load(episode)
      }
    >
      <Icon name={active ? "pause" : "play"} />
      {large && <span>{active ? t("pause") : t("listen")}</span>}
    </button>
  );
}
export function Transport() {
  const p = usePlayer(),
    { t } = useApp();
  return (
    <div className="transport" dir="ltr">
      <IconButton
        name="rewind"
        label={t("skipBack")}
        onClick={() => p.seek(p.ms - 15000)}
      />
      <button
        className="round-play main-play"
        onClick={p.toggle}
        aria-label={p.playing ? t("pause") : t("play")}
      >
        <Icon size={24} name={p.playing ? "pause" : "play"} />
      </button>
      <IconButton
        name="forward"
        label={t("skipForward")}
        onClick={() => p.seek(p.ms + 15000)}
      />
    </div>
  );
}
export function PlayerDock({
  hidden = false,
  compact = false,
}: {
  hidden?: boolean;
  compact?: boolean;
}) {
  const p = usePlayer(),
    { t, title, catalogue, lang, appearance } = useApp();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("ew-dock-collapsed") === "true";
    } catch {
      return false;
    }
  });
  const track = p.annotations ? currentTrack(p.annotations.tracks, p.ms) : null,
    cue = p.annotations?.cues.find(
      (c) => c.start_ms <= p.ms && p.ms < c.end_ms,
    );
  function revealPlayer(e: { preventDefault: () => void }) {
    if (
      !p.episode ||
      location.hash.split("?")[0] !== route("episode", p.episode.id)
    )
      return;
    e.preventDefault();
    const main = document.querySelector<HTMLElement>(".studio-player");
    main?.scrollIntoView({
      behavior:
        appearance.motion === "reduced" ||
        matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "instant"
          : "smooth",
      block: "start",
    });
    main
      ?.querySelector<HTMLButtonElement>(".main-play")
      ?.focus({ preventScroll: true });
  }
  return (
    <>
      {p.episode && !hidden && (
        <section
          className={
            "player-dock " +
            (compact ? "compact-dock " : "") +
            (collapsed ? "is-collapsed" : "")
          }
          aria-label={t("listening")}
        >
          <div
            className="dock-progress"
            style={{ width: `${(p.ms / Math.max(1, p.duration)) * 100}%` }}
          />
          <a
            className="dock-identity"
            href={route("episode", p.episode.id)}
            onClick={revealPlayer}
          >
            <Artwork episode={p.episode} />
            <div>
              <span className="eyebrow">
                {p.buffering ? t("loading") : t("listening")}
              </span>
              <strong>
                {(compact || collapsed) && track
                  ? trackIdentity(track).title || title(p.episode)
                  : title(p.episode)}
              </strong>
              <small>
                {track
                  ? trackIdentity(track).artist || p.episode.show_title
                  : p.episode.show_title}
              </small>
            </div>
          </a>
          <div className="dock-center">
            <Transport />
            <div className="dock-seek" dir="ltr">
              <span>{clock(p.ms)}</span>
              <input
                aria-label={t("seek")}
                type="range"
                min="0"
                max={p.duration || 1}
                value={p.ms}
                step="1000"
                onChange={(e) => p.seek(+e.target.value)}
              />
              <span>{clock(p.duration)}</span>
            </div>
          </div>
          <div className="dock-tools">
            {!compact && (
              <>
                <label className="speed">
                  <span className="sr-only">{t("speed")}</span>
                  <select
                    value={p.speed}
                    onChange={(e) => p.setSpeed(+e.target.value)}
                  >
                    {[0.75, 1, 1.25, 1.5, 2].map((n) => (
                      <option key={n} value={n}>
                        {n}×
                      </option>
                    ))}
                  </select>
                </label>
                <div className="volume">
                  <IconButton
                    name={p.volume ? "volume" : "muted"}
                    label={t("volume")}
                    onClick={() => p.setVolume(p.volume ? 0 : 0.8)}
                  />
                  <input
                    aria-label={t("volume")}
                    type="range"
                    min="0"
                    max="1"
                    step=".01"
                    value={p.volume}
                    onChange={(e) => p.setVolume(+e.target.value)}
                  />
                </div>
              </>
            )}
            <DeviceButton />
            <IconButton
              name="queue"
              label={`${t("next")} (${p.queue.length})`}
              onClick={() => p.setQueueOpen(true)}
            />
            <a
              className="icon-button"
              href={route("episode", p.episode.id)}
              aria-label={t("openPlayer")}
              onClick={revealPlayer}
            >
              <Icon name="expand" />
            </a>
            <button
              type="button"
              className="icon-button dock-collapse"
              aria-label={t(collapsed ? "expandPlayer" : "collapsePlayer")}
              title={t(collapsed ? "expandPlayer" : "collapsePlayer")}
              aria-expanded={!collapsed}
              onClick={() => {
                setCollapsed(!collapsed);
                persist("ew-dock-collapsed", String(!collapsed));
              }}
            >
              <Icon name="collapse" />
            </button>
          </div>
          {p.error && (
            <div className="dock-error" role="alert">
              {p.error}
            </div>
          )}
          {cue && !compact && (
            <div
              className="dock-caption"
              lang={cueLanguage(cue, lang)}
              dir="auto"
            >
              {cueText(cue, lang)}
            </div>
          )}
        </section>
      )}
      {p.queueOpen && (
        <Modal title={t("next")} onClose={() => p.setQueueOpen(false)}>
          <div className="queue-list">
            {p.queue.length === 0 ? (
              <p className="muted">{t("queueEmpty")}</p>
            ) : (
              p.queue.map((id, i) => {
                const ep = catalogue.episodes.find((e) => e.id === id);
                return ep ? (
                  <div className="queue-row" key={i}>
                    <span className="mono">{i + 1}</span>
                    <button
                      onClick={() => {
                        p.setQueue((q) => q.filter((_, n) => n !== i));
                        p.load(ep, 0);
                      }}
                    >
                      <strong>{title(ep)}</strong>
                      <small>{ep.show_title}</small>
                    </button>
                    <IconButton
                      name="close"
                      label={t("remove")}
                      onClick={() =>
                        p.setQueue((q) => q.filter((_, n) => n !== i))
                      }
                    />
                  </div>
                ) : null;
              })
            )}
          </div>
          {p.queue.length > 0 && (
            <button className="button" onClick={() => p.setQueue([])}>
              {t("clear")}
            </button>
          )}
        </Modal>
      )}
    </>
  );
}
export function DeviceButton() {
  const p = usePlayer(),
    { t } = useApp();
  return (
    <IconButton
      name="cast"
      label={t("listenDevices")}
      onClick={() => p.setDevicesOpen(true)}
    />
  );
}
export function Caption({ cue }: { cue: Cue | undefined }) {
  const { lang, t } = useApp();
  return (
    <div className={"live-subtitles " + (!cue ? "waiting" : "")}>
      <div className="subtitle-heading">
        <span>
          <span className="live-dot" />
          {t("liveSubtitles")}
        </span>
        <LanguageSelect />
      </div>
      {cue && (
        <p dir="auto" lang={cueLanguage(cue, lang)} key={cue.id}>
          {cueText(cue, lang)}
        </p>
      )}
      {cue &&
        lang !== "original" &&
        lang !== cue.lang &&
        !cue.translations[lang] && (
          <span className="translation-note">{t("captionMissing")}</span>
        )}
    </div>
  );
}
export function PlayerLanguage() {
  return <LanguageSelect />;
}
